import type { IServerSideDatasource } from 'ag-grid-enterprise';

import { registerAgGrid } from './agGridRegistry';
import { apiClient } from './api-client';
import { queryClient } from './query-client';

/**
 * Factory to create a reusable AG Grid server-side datasource using fetch.
 * Handles sorting, filtering, pagination, and error handling with advanced filter support.
 *
 * @param url - The full API endpoint URL for the resource.
 * @param getRequestBody - Optional function to customize the request body.
 * @returns IServerSideDatasource for AG Grid
 */
interface AgGridPaginatedResponse<T> {
  total: number;
  records: T[];
}

// ---------------------------------------------------------------------------
// AG Grid filter model shapes
// AG Grid serialises set-filter values as strings regardless of the source
// type, so all set/legacy filter configs carry string[] values.
// ---------------------------------------------------------------------------

interface AgTextFilterConfig {
  filterType: 'text';
  type: string;
  filter?: string;
}

interface AgNumberFilterConfig {
  filterType: 'number';
  type: string;
  filter: number | null;
  filterTo: number | null;
}

interface AgDateFilterConfig {
  filterType: 'date';
  type: string;
  dateFrom: string | null;
  dateTo: string | null;
}

/** AG Grid Set Filter — values are always serialised as strings by AG Grid */
interface AgSetFilterConfig {
  filterType: 'set';
  values: string[];
}

/**
 * Legacy set filter format that has no `filterType` discriminant.
 * `filterType?: never` ensures it is excluded from the discriminated branches.
 */
interface AgLegacySetFilterConfig {
  filterType?: never;
  values: string[];
}

/** AG Grid v32+ compound filter: `{ filterType, operator, conditions[] }` */
interface AgCompoundFilterConfig {
  filterType: 'text' | 'number' | 'date';
  operator: 'AND' | 'OR';
  conditions: Array<AgTextFilterConfig | AgNumberFilterConfig | AgDateFilterConfig>;
}

type AgFilterConfig =
  | AgTextFilterConfig
  | AgNumberFilterConfig
  | AgDateFilterConfig
  | AgSetFilterConfig
  | AgLegacySetFilterConfig
  | AgCompoundFilterConfig;

// ---------------------------------------------------------------------------
// Parsed output types sent to the API
// ---------------------------------------------------------------------------

/** A single filter condition in the API request body */
export interface FilterCondition {
  operator: string;
  value: string | number | boolean | null;
  /**
   * Inclusive upper bound for numeric `between` conditions (`NumericFilterCondition.value_to`).
   * Only populated when `operator === 'between'` on a number column.
   * Note: numeric ranges use `value` + `value_to`; date ranges use `start_value` + `end_value` —
   * two different API discriminants that both map to the `between` operator.
   */
  value_to?: number | null;
  /**
   * Inclusive range start for `between` date conditions.
   * Only populated when `operator === 'between'`; absent for all other operators.
   */
  start_value?: string | null;
  /**
   * Inclusive range end for `between` date conditions.
   * Only populated when `operator === 'between'`; absent for all other operators.
   */
  end_value?: string | null;
}

/** A parsed filter entry for one column, sent to the API */
export interface ParsedFilter {
  conditions: FilterCondition[];
  logic: 'and' | 'or';
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Operator maps — translate AG Grid's camelCase type strings to the API's
// snake_case operator names.
// ---------------------------------------------------------------------------

const TEXT_OPERATOR_MAP: Record<string, string> = {
  contains: 'contains',
  notContains: 'not_contains',
  equals: 'equals',
  notEqual: 'not_equals',
  startsWith: 'starts_with',
  endsWith: 'ends_with',
  blank: 'blank',
  notBlank: 'not_blank',
};

const NUMBER_OPERATOR_MAP: Record<string, string> = {
  equals: 'equals',
  notEqual: 'not_equals',
  lessThan: 'less_than',
  lessThanOrEqual: 'less_than_or_equals',
  greaterThan: 'greater_than',
  greaterThanOrEqual: 'greater_than_or_equals',
  blank: 'blank',
  notBlank: 'not_blank',
};

const DATE_OPERATOR_MAP: Record<string, string> = {
  equals: 'equals',
  notEqual: 'not_equals',
  // The API only exposes exclusive boundary operators ('before' = <, 'after' = >).
  // There are no on_or_before / on_or_after variants, so lessThanOrEqual and
  // greaterThanOrEqual are approximated with the closest available operator.
  // This is a known API limitation for single-value date comparisons.
  lessThan: 'before',
  lessThanOrEqual: 'before',
  greaterThan: 'after',
  greaterThanOrEqual: 'after',
  // Range filters use the dedicated 'between' operator (inclusive on both ends)
  // handled explicitly in parseFilterModel — this entry is for completeness.
  inRange: 'between',
  blank: 'blank',
  notBlank: 'not_blank',
};

/**
 * AG Grid's Set Filter always serialises values as strings in the filter model,
 * regardless of the types supplied in `filterParams.values`. Pass the column IDs
 * of any boolean fields here so that `"true"`/`"false"` strings are converted
 * back to proper booleans before the payload is sent to the API.
 */
function coerceSetValue(value: string, isBooleanField: boolean): string | boolean {
  if (isBooleanField && (value === 'true' || value === 'false')) {
    return value === 'true';
  }
  return value;
}

// ---------------------------------------------------------------------------
// Single-condition parsers — each returns 0-N FilterCondition entries
// ---------------------------------------------------------------------------

function parseTextCondition(config: AgTextFilterConfig): FilterCondition[] {
  const { type, filter } = config;
  const operator = TEXT_OPERATOR_MAP[type] ?? 'contains';
  const isBlankOp = operator === 'blank' || operator === 'not_blank';
  if (isBlankOp) return [{ operator, value: null }];
  if (filter) return [{ operator, value: filter }];
  return [];
}

function parseNumberCondition(config: AgNumberFilterConfig): FilterCondition[] {
  const { type, filter, filterTo } = config;
  const operator = NUMBER_OPERATOR_MAP[type] ?? 'equals';
  const isBlankOp = operator === 'blank' || operator === 'not_blank';

  if (type === 'inRange') {
    if (filter !== null && filterTo !== null) {
      // Use a single inclusive BETWEEN condition so range semantics are preserved
      // even when AG Grid combines conditions with an OR operator.
      return [{ operator: 'between', value: filter, value_to: filterTo }];
    }
    // Partial range (one bound null) — drop the condition rather than silently
    // falling through to the `?? 'equals'` default, which would be incorrect.
    return [];
  }
  if (isBlankOp) return [{ operator, value: null }];
  // Guard both null and undefined: AG Grid may emit either when the input is cleared.
  if (filter !== null && filter !== undefined) return [{ operator, value: filter }];
  return [];
}

function parseDateCondition(config: AgDateFilterConfig): FilterCondition[] {
  const { type, dateFrom, dateTo } = config;
  const operator = DATE_OPERATOR_MAP[type] ?? 'equals';
  const isBlankOp = operator === 'blank' || operator === 'not_blank';

  if (type === 'inRange' && dateFrom && dateTo) {
    // 'between' is inclusive on both ends; two separate after/before conditions would be exclusive.
    return [{ operator: 'between', value: null, start_value: dateFrom, end_value: dateTo }];
  }
  if (isBlankOp) return [{ operator, value: null }];
  if (dateFrom) return [{ operator, value: dateFrom }];
  return [];
}

// Type guard: compound filter has `conditions[]`; simple filters do not.
function isCompoundFilter(config: AgFilterConfig): config is AgCompoundFilterConfig {
  return (
    'conditions' in config && Array.isArray((config as { conditions?: unknown }).conditions) && 'operator' in config
  );
}

/**
 * Parse AG Grid's filter model into the API's query-friendly format.
 * Handles text, number, date, and set filters — including the compound
 * multi-condition shape introduced in AG Grid v32 (`{ operator, conditions[] }`).
 */
/**
 * Exported so a non-grid caller (e.g. a CSV export button) can send exactly the filters the grid
 * is displaying. Unlike `convertAgGridFiltersToApiFilters`, this is field-agnostic — it keys off
 * whatever colIds the grid supplies instead of a per-entity whitelist.
 */
export function parseFilterModel(
  filterModel: Record<string, AgFilterConfig | null | undefined>,
  booleanFields: ReadonlySet<string> = new Set(),
): Record<string, ParsedFilter> {
  const filters: Record<string, ParsedFilter> = {};

  for (const [colId, filterConfig] of Object.entries(filterModel)) {
    if (!filterConfig) continue;

    // Compound filter: { filterType, operator, conditions[] } — AG Grid v32+
    if (isCompoundFilter(filterConfig)) {
      const logic: 'and' | 'or' = filterConfig.operator === 'OR' ? 'or' : 'and';
      const conditions: FilterCondition[] = [];

      for (const condition of filterConfig.conditions) {
        if (condition.filterType === 'text') {
          conditions.push(...parseTextCondition(condition));
        } else if (condition.filterType === 'number') {
          conditions.push(...parseNumberCondition(condition));
        } else if (condition.filterType === 'date') {
          conditions.push(...parseDateCondition(condition));
        }
      }

      if (conditions.length > 0) {
        // The API rejects `and` combined with multiple `equals` on one column as contradictory
        // (422) — and it IS unsatisfiable, since a value cannot equal two different things. AG
        // Grid lets a user build exactly that via a compound filter, so read it as the only
        // interpretation that can match anything rather than letting the grid error out.
        const equalsCount = conditions.filter(c => c.operator === 'equals').length;
        const effectiveLogic = logic === 'and' && equalsCount > 1 ? 'or' : logic;

        filters[colId] = { conditions, logic: effectiveLogic };
      }
      continue;
    }

    // Simple (single-condition) filter
    if (filterConfig.filterType === 'text') {
      const conditions = parseTextCondition(filterConfig);
      if (conditions.length > 0) {
        filters[colId] = { conditions, logic: 'and' };
      }
    } else if (filterConfig.filterType === 'number') {
      const conditions = parseNumberCondition(filterConfig);
      if (conditions.length > 0) {
        filters[colId] = { conditions, logic: 'and' };
      }
    } else if (filterConfig.filterType === 'date') {
      const conditions = parseDateCondition(filterConfig);
      if (conditions.length > 0) {
        filters[colId] = { conditions, logic: 'and' };
      }
    } else if (filterConfig.filterType === 'set') {
      const { values: selectedValues } = filterConfig;
      if (selectedValues.length > 0) {
        const isBooleanField = booleanFields.has(colId);
        filters[colId] = {
          conditions: selectedValues.map(value => ({
            operator: 'equals',
            // AG Grid serialises set-filter values as strings; coerce back to
            // boolean for fields declared as boolean so the API receives the
            // correct primitive type (true / false, not "true" / "false").
            value: coerceSetValue(value, isBooleanField),
          })),
          logic: 'or',
        };
      }
    } else if ('values' in filterConfig && Array.isArray(filterConfig.values)) {
      // Fallback for legacy set filter format (no filterType discriminant)
      const { values: selectedValues } = filterConfig;
      if (selectedValues.length > 0) {
        const isBooleanField = booleanFields.has(colId);
        filters[colId] = {
          conditions: selectedValues.map(value => ({
            operator: 'equals',
            value: coerceSetValue(value, isBooleanField),
          })),
          logic: 'or',
        };
      }
    }
  }

  return filters;
}

/**
 * Convert sort model to query parameters
 */
function parseSortModel(sortModel: Array<{ colId: string; sort: 'asc' | 'desc' }>): {
  sortBy: string;
  sortOrder: 'asc' | 'desc';
} {
  if (!sortModel || sortModel.length === 0) {
    return { sortBy: 'created_at', sortOrder: 'desc' };
  }

  const firstSort = sortModel[0];
  return {
    sortBy: firstSort.colId,
    sortOrder: firstSort.sort || 'asc',
  };
}

/**
 * Returns true when the API rejected the request specifically because the
 * `sort_by` value was not a member of the expected enum.
 *
 * FastAPI emits a 422 with `detail[].loc` containing `"sort_by"` when the
 * value doesn't match the server-side enum.  We use this to distinguish a
 * sort-field rejection from any other validation failure so that we can retry
 * with the default sort instead of surfacing an error to the user.
 */
function isSortByValidationError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false;
  const { status, data } = error as { status?: number; data?: unknown };
  if (status !== 422) return false;
  const detail = (data as { detail?: Array<{ loc?: unknown[] }> } | undefined)?.detail;
  // Match FastAPI's loc path exactly: ["body", "sort_by"].  Checking both
  // position and value avoids silently swallowing unrelated 422s that happen
  // to mention a field named sort_by elsewhere in the payload.
  return (
    Array.isArray(detail) && detail.some(d => Array.isArray(d.loc) && d.loc[0] === 'body' && d.loc[1] === 'sort_by')
  );
}

export interface AgGridDatasourceOptions<TResponse = unknown, TRequestBody = Record<string, unknown>> {
  /**
   * Column IDs whose values should be treated as booleans.
   *
   * AG Grid's Set Filter always serialises filter-model values as strings, so
   * `true` becomes `"true"` and `false` becomes `"false"` by the time the model
   * reaches the datasource. Listing a field here causes `parseFilterModel` to
   * convert those strings back to proper booleans before the request is sent.
   *
   * @example { booleanFields: ['is_super_admin', 'is_active'] }
   */
  booleanFields?: string[];

  /**
   * How long (in milliseconds) a cached datasource response is considered fresh.
   *
   * When the grid re-mounts after navigation (e.g. the user goes to another page
   * and comes back), `getRows` will be satisfied instantly from the TanStack Query
   * cache if the data is still within this window — avoiding the loading overlay
   * entirely.  Set to `0` to always re-fetch, or `Infinity` to never re-fetch
   * until the cache is explicitly invalidated.
   *
   * The default is intentionally long (1 day) so that navigation between pages
   * is always instant. Use `useAgGridDatasourceRefresh` to give users an explicit
   * "Refresh" action that invalidates this cache and purges the grid in one call.
   *
   * @default 86_400_000 (1 day)
   */
  staleTime?: number;

  /**
   * How long (in milliseconds) inactive cached datasource responses persist in memory
   * before garbage collection removes them.
   *
   * When all grid instances unmount, the cache entries remain available for `gcTime`
   * duration. If the user navigates back within this window, the cached pages are
   * instantly available (even if stale). After `gcTime` expires, the cache is
   * permanently deleted, forcing a refetch on the next load.
   *
   * The default (5 minutes) may be too short for navigation patterns where users
   * leave and return to study details after a longer gap. Set to 30+ minutes to
   * retain table data across typical user workflows.
   *
   * @default 1_800_000 (30 minutes)
   */
  gcTime?: number;

  /**
   * Optional service-layer fetcher used instead of `apiClient.post(url, body)`.
   *
   * This keeps AG Grid request parsing/caching centralized while allowing
   * callers to route requests through domain services.
   */
  fetcher?: (body: TRequestBody) => Promise<TResponse | null>;

  /**
   * Called after every successful fetch with the row count for the current
   * filter/sort state (i.e. `response.total`). Use this to keep an external
   * "N results" label in sync with the grid's live filtered count instead of
   * a separate, unfiltered total.
   */
  onTotalChanged?: (total: number) => void;

  /**
   * Called when a fetch fails, so a caller keeping an external count label can
   * distinguish "failed" from "still loading" — without this, a first-load
   * failure leaves such a label on its loading placeholder forever, and a later
   * failure leaves the last successful total on screen as if it were current.
   */
  onTotalError?: () => void;

  /**
   * Every successful response, for envelope fields beyond `total`. Not the same as a side effect
   * in `fetcher`: that is the `queryFn`, which a fresh cache hit skips — this fires either way.
   */
  onResponse?: (response: TResponse) => void;

  /**
   * Called when a requested sort column isn't supported by the backend and
   * the grid has fallen back to the default sort instead. Receives the
   * `colId` of the rejected sort column (if available).
   *
   * This helper intentionally has no UI/Toaster dependency, so it's up to
   * the caller to decide whether/when to surface feedback — e.g. gating
   * notifications to genuine user-initiated sort clicks (rather than a
   * persisted sort restored from localStorage on mount) and deduping
   * repeat calls for the same sort attempt (AG Grid may issue multiple
   * `getRows` requests per sort).
   */
  onSortFallback?: (colId: string | undefined) => void;
}

/**
 * Returns the TanStack Query key prefix used by `createAgGridDatasource` for a
 * given endpoint URL.
 *
 * Pass this to `queryClient.invalidateQueries` to mark every cached page /
 * sort / filter combination for that grid as stale, then call
 * `gridRef.current?.api.refreshServerSide({ purge: true })` to trigger a
 * reload.  `useAgGridDatasourceRefresh` does both steps for you.
 *
 * @example
 * ```typescript
 * queryClient.invalidateQueries({
 *   queryKey: agGridDatasourceQueryKey(userQueryKeys.endpoints.globalSearch()),
 * });
 * ```
 */
export function agGridDatasourceQueryKey(url: string) {
  return ['ag-grid-datasource', url] as const;
}

/** Parameters passed to the optional `getRequestBody` factory */
export interface GetRequestBodyParams {
  skip: number;
  limit: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  filters: Record<string, ParsedFilter>;
}

export function createAgGridDatasource<
  TRecord,
  TResponse extends AgGridPaginatedResponse<TRecord>,
  TRequestBody = Record<string, unknown>,
>(
  url: string,
  getRequestBody?: (params: GetRequestBodyParams) => TRequestBody,
  options?: AgGridDatasourceOptions<TResponse, TRequestBody>,
): IServerSideDatasource {
  const booleanFieldSet = new Set(options?.booleanFields ?? []);
  const staleTime = options?.staleTime ?? 86_400_000;
  const gcTime = options?.gcTime ?? 1_800_000; // 30 minutes default

  return {
    getRows: async params => {
      // Every server-side grid reaches this function, making it the one place an app-wide repaint
      // can find them all (agGridRegistry.ts). Guarded: a throw here would escape before either
      // params.success() or params.fail() runs, leaving the grid on a loading overlay forever.
      try {
        registerAgGrid(params.api);
      } catch (error) {
        console.error('Failed to register grid for app-wide refresh; it will not repaint', error);
      }

      // Extract filtering and pagination (same for every attempt)
      const sortModel = params.request.sortModel ?? [];
      const filterModel = (params.request.filterModel ?? {}) as Record<string, AgFilterConfig | null | undefined>;
      const filters = parseFilterModel(filterModel, booleanFieldSet);
      const skip = params.request.startRow ?? 0;
      const limit = (params.request.endRow ?? 5) - skip;

      const buildBody = (overrideSortModel: typeof sortModel = sortModel): TRequestBody => {
        const { sortBy, sortOrder } = parseSortModel(overrideSortModel);
        if (getRequestBody) {
          return getRequestBody({ skip, limit, sortBy, sortOrder, filters });
        }

        return {
          skip,
          limit,
          sort_by: sortBy,
          sort_order: sortOrder,
          filters: Object.keys(filters).length > 0 ? filters : undefined,
        } as unknown as TRequestBody;
      };

      // Use TanStack Query's cache so that re-navigating to a page that has
      // already loaded this grid avoids the loading overlay entirely.
      // fetchQuery resolves immediately when the cached entry is still fresh
      // (within `staleTime`), making getRows appear synchronous to AG Grid.
      //
      // `retry` is optional: pass `0` on the primary request so a 422
      // sort-field rejection surfaces immediately rather than being retried
      // before the sort-fallback logic can fire.  Omit it on the fallback —
      // note that `fetchQuery` (unlike `useQuery`) defaults `retry` to `false`,
      // so the fallback will also not retry on transient errors; it will simply
      // re-throw, which is caught by the outer catch and surfaces via params.fail().
      const fetchBody = (body: TRequestBody, retry?: number) =>
        queryClient.fetchQuery<TResponse | null>({
          queryKey: ['ag-grid-datasource', url, body],
          queryFn: () => (options?.fetcher ? options.fetcher(body) : apiClient.post<TResponse>(url, body)),
          staleTime,
          gcTime,
          ...(retry !== undefined ? { retry } : {}),
        });

      try {
        let response: TResponse | null;

        try {
          response = await fetchBody(buildBody(), 0);
        } catch (originalError) {
          // If the API rejected the sort field (column not yet supported by the
          // backend, e.g. computed columns like num_samples — see
          // nautilusbio/portal-backend#27), retry with the default sort so the
          // grid still loads data rather than showing ERR. When the backend adds
          // support for the field the primary request will succeed and this
          // branch is skipped.
          if (isSortByValidationError(originalError) && sortModel.length > 0) {
            try {
              response = await fetchBody(buildBody([]));
              // The chosen sort column is not yet supported by the backend.
              // The grid has loaded successfully with the default sort instead —
              // log so that operators know a column's sort support is missing,
              // and tell the user so they don't think the click did nothing.
              console.warn(
                'AG Grid: sort column not supported by API; falling back to default sort.',
                sortModel[0]?.colId,
              );
              options?.onSortFallback?.(sortModel[0]?.colId);
            } catch {
              // The fallback also failed (likely a transient error).  Surface
              // the original sort-rejection error — not the fallback error —
              // so the logged message points to the actual root cause.
              console.warn('AG Grid sort fallback also failed; surfacing original sort-rejection error.');
              throw originalError;
            }
          } else {
            throw originalError;
          }
        }

        const rowCount = response?.total ?? 0;
        params.success({
          rowCount,
          rowData: response?.records ?? [],
        });
        options?.onTotalChanged?.(rowCount);
        // A 204 has no envelope to report, so an `onResponse` consumer keeps whatever it last
        // showed — unlike `onTotalChanged` above, which has 0 to fall back on.
        if (response) options?.onResponse?.(response);
      } catch (error) {
        console.error('AG Grid datasource fetch error:', error);
        options?.onTotalError?.();
        params.fail();
      }
    },
  };
}
